import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { unorderedStringArraysEqual } from "@/lib/settingsCardDirty";

const ALLOWLIST_QUERY_KEY = ["documents", "inbound-email-allowlist"] as const;
const STAFF_USERS_QUERY_KEY = ["notifications", "staff-user-options"] as const;

type AllowlistUser = { userId: string; email: string | null; displayName: string | null };

function fetchAllowlist(): Promise<{ mailbox: string; users: AllowlistUser[] }> {
  return apiFetch("/api/document-inbound-email-allowlist");
}

function saveAllowlist(userIds: string[]): Promise<{ mailbox: string; users: AllowlistUser[] }> {
  return apiFetch("/api/document-inbound-email-allowlist", {
    method: "PUT",
    body: { userIds },
  });
}

function fetchStaffUsers(): Promise<{
  users: Array<{ userId: string; email: string; displayName: string | null }>;
}> {
  return apiFetch("/api/notifications/staff-user-options");
}

export function DocumentInboundEmailAllowlistSection(props: { canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const allowlistQuery = useQuery({
    queryKey: ALLOWLIST_QUERY_KEY,
    queryFn: fetchAllowlist,
  });
  const usersQuery = useQuery({
    queryKey: STAFF_USERS_QUERY_KEY,
    queryFn: fetchStaffUsers,
  });

  useEffect(() => {
    if (allowlistQuery.data != null && !initialized) {
      setSelectedUserIds(allowlistQuery.data.users.map((user) => user.userId));
      setInitialized(true);
    }
  }, [allowlistQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => saveAllowlist(selectedUserIds),
    onSuccess: (data) => {
      queryClient.setQueryData(ALLOWLIST_QUERY_KEY, data);
      setSelectedUserIds(data.users.map((user) => user.userId));
      setInitialized(true);
    },
  });

  const users = usersQuery.data?.users ?? [];
  const persistedUserIds = useMemo(
    () => allowlistQuery.data?.users.map((user) => user.userId) ?? [],
    [allowlistQuery.data?.users],
  );
  const isDirty = useMemo(() => {
    if (!initialized || allowlistQuery.data == null) return false;
    return !unorderedStringArraysEqual(selectedUserIds, persistedUserIds);
  }, [initialized, allowlistQuery.data, persistedUserIds, selectedUserIds]);

  return (
    <HubSettingsSection
      title="Inbound drawings email"
      hoverHint="Only these hub users may send files to drawings@smartgridsolutions.com. Attachments become person-owned WIP files that anyone with Document Control access can see."
    >
      {allowlistQuery.isLoading || usersQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading hub users…</p>
      ) : allowlistQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {allowlistQuery.error instanceof ApiError
            ? allowlistQuery.error.message
            : "Could not load the inbound email allowlist."}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-600">No hub users available for selection.</p>
      ) : (
        <HubUserSearchableMultiSelect
          id="document-inbound-email-allowlist"
          users={users}
          selectedUserIds={selectedUserIds}
          onSelectedUserIdsChange={setSelectedUserIds}
          disabled={!props.canEdit}
          placeholder="Select senders…"
          noneSelectedLabel="No senders"
        />
      )}

      <HubSettingsFooter>
        <ReadOnlyHoverLayer
          active={!props.canEdit}
          hoverHint="Document Control administrators can edit who may email drawings@smartgridsolutions.com."
          layout="inline"
        >
          <div className="space-y-2">
            {saveMutation.isError ? (
              <p className="text-sm text-rose-700" role="alert">
                {saveMutation.error instanceof ApiError
                  ? saveMutation.error.message
                  : saveMutation.error instanceof Error
                    ? saveMutation.error.message
                    : "Could not save the inbound email allowlist."}
              </p>
            ) : null}
            <Button
              type="button"
              variant="primary"
              disabled={!props.canEdit || !isDirty || saveMutation.isPending || allowlistQuery.isLoading}
              isLoading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save inbound email senders
            </Button>
          </div>
        </ReadOnlyHoverLayer>
      </HubSettingsFooter>
    </HubSettingsSection>
  );
}
